import { useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Bank, Plus, ArrowClockwise, Trash, WarningCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { fmtMoney } from "../../lib/finance-format";

/**
 * BankConnectionsPanel — QuickBooks-style read-only bank sync via Plaid.
 *
 * Flow:
 *   1. Owner clicks "Conectar banco" → we POST /plaid/link-token/create
 *      and hand the token to Plaid's Link widget (usePlaidLink).
 *   2. On onSuccess we POST /plaid/item/exchange with the returned
 *      public_token; backend stores an encrypted access_token.
 *   3. The panel then lists connected banks, cached accounts + live balances,
 *      and offers a "Sincronizar" button that pulls fresh transactions.
 *
 * Never sees credentials — Plaid handles bank auth in an iframe.
 */
export default function BankConnectionsPanel() {
  const [linkToken, setLinkToken] = useState(null);
  const [items, setItems] = useState([]);
  const [accountsByItem, setAccountsByItem] = useState({});
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const [itemsRes, accountsRes] = await Promise.all([
        api.get("/finance/plaid/items"),
        api.get("/finance/plaid/accounts"),
      ]);
      setItems(itemsRes.data.items || []);
      const map = {};
      for (const b of accountsRes.data.items || []) map[b.item_id] = b.accounts;
      setAccountsByItem(map);
    } catch (err) {
      // Empty state is fine; only toast when Plaid itself is misconfigured.
      if (err.response?.status && err.response.status >= 500) {
        toast.error("No se pudo cargar el estado de bancos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mintLinkToken = async () => {
    if (minting || linkToken) return;
    setMinting(true);
    try {
      const { data } = await api.post("/finance/plaid/link-token/create");
      setLinkToken(data.link_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar la conexión");
    } finally {
      setMinting(false);
    }
  };

  // Retry sync up to N times while Plaid Sandbox drips transactions in the
  // background (the initial pull can take 30-60s). Bails as soon as any
  // item reports added > 0 or after the loop expires.
  const primeInitialSync = async () => {
    const MAX = 6, WAIT_MS = 5000;
    for (let i = 0; i < MAX; i++) {
      try {
        const { data } = await api.post("/finance/plaid/transactions/sync");
        const added = (data.results || []).reduce((a, r) => a + (r.added || 0), 0);
        if (added > 0 || i === MAX - 1) {
          if (added > 0) toast.success(`Sincronizado · +${added} transacciones nuevas`);
          break;
        }
      } catch {
        // Network hiccup — retry on next tick
      }
      await new Promise((r) => setTimeout(r, WAIT_MS));
    }
    await load();
  };

  const onSuccess = async (public_token, metadata) => {
    try {
      const { data } = await api.post("/finance/plaid/item/exchange", {
        public_token,
        institution_name: metadata?.institution?.name || null,
        institution_id:   metadata?.institution?.institution_id || null,
      });
      toast.success(`Banco conectado · ${data.institution_name || "Cuenta"}`);
      await load();
      setSyncing(true);
      toast.info("Cargando transacciones... esto puede tardar hasta 1 min", { duration: 6000 });
      primeInitialSync().finally(() => setSyncing(false));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo al intercambiar token");
    } finally {
      setLinkToken(null);
    }
  };

  const onExit = (err) => {
    if (err) toast.error(err.display_message || err.error_message || "Conexión cancelada");
    setLinkToken(null);
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/finance/plaid/transactions/sync");
      const summary = (data.results || []).reduce(
        (a, r) => ({ added: a.added + r.added, modified: a.modified + r.modified, removed: a.removed + r.removed }),
        { added: 0, modified: 0, removed: 0 },
      );
      toast.success(
        `Sincronizado · +${summary.added} nuevas · ${summary.modified} actualizadas`,
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async (it) => {
    if (!window.confirm(`¿Desconectar ${it.institution_name || "este banco"}? Se eliminarán las transacciones sincronizadas.`)) return;
    try {
      await api.delete(`/finance/plaid/items/${it.item_id}`);
      toast.success("Banco desconectado");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desconectar");
    }
  };

  const setAccountScope = async (accountId, scope) => {
    // Optimistic update so the toggle feels instant
    setAccountsByItem((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        next[k] = next[k].map((a) => (a.account_id === accountId ? { ...a, scope } : a));
      }
      return next;
    });
    try {
      const { data } = await api.put(`/finance/plaid/accounts/${accountId}/scope`, { scope });
      toast.success(
        scope === "business"
          ? `Cuenta marcada como Negocio · ${data.transactions_updated} transacción(es) actualizadas`
          : `Cuenta marcada como Personal · ${data.transactions_updated} transacción(es) actualizadas`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar el uso de la cuenta");
      await load();  // revert on failure
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            BANCOS CONECTADOS
          </div>
          <div className="text-lg font-heading font-bold text-white mt-0.5">
            Sync automático estilo QuickBooks
          </div>
          <div className="text-[11px] font-mono text-white/50 mt-0.5">
            Solo lectura · nunca vemos tu contraseña · el sync corre a diario
          </div>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button
              data-testid="plaid-sync-btn"
              onClick={syncAll}
              disabled={syncing}
              className="flex items-center gap-1 rounded-md bg-white/5 hover:bg-white/12 disabled:opacity-40 text-white/80 hover:text-white text-[10px] font-mono uppercase tracking-widest px-3 py-2"
            >
              <ArrowClockwise size={12} weight="bold" className={syncing ? "animate-spin" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </button>
          )}
          <button
            data-testid="plaid-connect-btn"
            onClick={mintLinkToken}
            disabled={minting || !!linkToken}
            className="on-solid flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[11px] font-mono font-bold uppercase tracking-widest px-3 py-2"
          >
            <Plus size={12} weight="bold" />
            Conectar banco
          </button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-white/8 bg-black/20 p-8 text-center text-[11px] font-mono text-white/40">
          Cargando bancos...
        </div>
      ) : items.length === 0 ? (
        <EmptyState onConnect={mintLinkToken} minting={minting || !!linkToken} />
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <BankCard
              key={it.item_id}
              item={it}
              accounts={accountsByItem[it.item_id] || []}
              onDisconnect={() => disconnect(it)}
              onScopeChange={setAccountScope}
            />
          ))}
        </div>
      )}

      {/* Single Plaid Link instance — only mounted when we actually have a token
          so the Plaid script is embedded exactly once per open. */}
      {linkToken && (
        <PlaidLinkLauncher token={linkToken} onSuccess={onSuccess} onExit={onExit} />
      )}
    </div>
  );
}

// Isolated subcomponent so usePlaidLink is only instantiated with a real token
// (Plaid warns "link-initialize.js embedded more than once" if the hook is
// re-mounted with a changing token).
function PlaidLinkLauncher({ token, onSuccess, onExit }) {
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });
  useEffect(() => { if (ready) open(); }, [ready, open]);
  return null;
}

function EmptyState({ onConnect, minting }) {
  return (
    <div
      data-testid="plaid-empty-state"
      className="rounded-2xl border border-dashed border-white/12 bg-black/20 p-8 text-center"
    >
      <Bank size={36} weight="fill" className="mx-auto mb-3 text-white/25" />
      <div className="text-[13px] font-heading font-bold text-white mb-1">
        Conecta tu primer banco
      </div>
      <div className="text-[11px] font-mono text-white/50 max-w-sm mx-auto mb-4">
        Enlaza Chase, BofA, Wells Fargo o cualquier banco compatible con Plaid.
        Solo lectura. Vemos las transacciones para cruzarlas contra tus recibos
        automáticamente — nunca tu contraseña.
      </div>
      <button
        data-testid="plaid-empty-connect"
        onClick={onConnect}
        disabled={minting}
        className="on-solid inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[11px] font-mono font-bold uppercase tracking-widest px-4 py-2"
      >
        <Sparkle size={12} weight="fill" />
        {minting ? "Preparando..." : "Empezar (Sandbox)"}
      </button>
    </div>
  );
}

function BankCard({ item, accounts, onDisconnect, onScopeChange }) {
  const total = accounts.reduce(
    (s, a) => s + Number(a?.balances?.available ?? a?.balances?.current ?? 0), 0,
  );
  return (
    <div
      data-testid={`plaid-bank-${item.item_id}`}
      className="rounded-xl border border-white/8 bg-black/25 p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Bank size={16} weight="fill" className="text-emerald-300" />
          </div>
          <div>
            <div className="text-[14px] font-heading font-bold text-white">
              {item.institution_name || "Banco"}
            </div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
              {item.transaction_count || 0} transacciones · sync {item.last_sync_at ? new Date(item.last_sync_at).toLocaleString() : "nunca"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-white/50">Total disponible:</span>
          <span className="text-[15px] font-heading font-bold text-emerald-300 tabular-nums">
            {fmtMoney(total)}
          </span>
          <button
            data-testid={`plaid-disconnect-${item.item_id}`}
            onClick={onDisconnect}
            className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-white/6 transition"
            title="Desconectar"
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <div className="rounded border border-white/8 bg-black/30 p-3 text-[10px] font-mono text-white/40 flex items-center gap-2">
          <WarningCircle size={12} weight="bold" />
          Sincroniza para ver las cuentas
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {accounts.map((a) => (
            <div key={a.account_id} className="rounded border border-white/8 bg-black/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12px] font-heading font-bold text-white truncate">
                    {a.name || a.official_name || "Cuenta"}
                  </div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                    {a.subtype || a.type} · ****{a.mask || "----"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[13px] font-heading font-bold text-white tabular-nums">
                    {fmtMoney(a.balances?.available ?? a.balances?.current ?? 0)}
                  </div>
                  <div className="text-[9px] font-mono text-white/40">
                    {a.balances?.iso_currency_code || "USD"}
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-white/8">
                <ScopeToggle
                  account={a}
                  onChange={(scope) => onScopeChange(a.account_id, scope)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScopeToggle({ account, onChange }) {
  const scope = account.scope || "personal";
  const btn = (val, label) => (
    <button
      key={val}
      data-testid={`plaid-acct-scope-${account.account_id}-${val}`}
      onClick={() => scope !== val && onChange(val)}
      className={
        "flex-1 rounded px-2 py-1 text-[10px] font-mono font-bold uppercase tracking-widest transition " +
        (scope === val
          ? (val === "business"
              ? "bg-amber-500/25 text-amber-800 dark:text-amber-200 border border-amber-500/60"
              : "bg-sky-500/25 text-sky-800 dark:text-sky-200 border border-sky-500/60")
          : "text-slate-500 dark:text-white/40 hover:text-slate-800 dark:hover:text-white/70 border border-transparent")
      }
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1">
      <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500 dark:text-white/40 mr-1">Uso:</span>
      {btn("personal", "Personal")}
      {btn("business", "Negocio")}
    </div>
  );
}
